import { $appDigest, $unwatch, isChangeFromWatch, isDefined, isObject, resetChangeFromWatch, toBoolean, toDimension } from '@wm/core';

import {BaseComponent} from "../common/base/base.component";
import {getConditionalClasses, getWatchIdentifier} from "../../utils/widget-utils";
import {getWidgetPropsByType} from "./widget-props";
import { isStyle } from './styler';
import { isBooleanAttr, isDimensionProp } from './constants';
import { get, isString, set, startsWith } from 'lodash-es';


/**
 * Returns the parsed value based on the provided type
 * if the type is PROP_TYPE.NUMBER returns a number/NaN
 * if the type is PROP_BOOLEAN returns true/false
 * else returns the same value without any type conversion
 *
 * @param {string} key
 * @param value
 * @param type
 * @returns {any}
 */
const parseValue = (key: string, value: any, type: string): any => {
  if (isDefined(value) && typeof(value) === 'string' && value.startsWith('bind:')) {
    return value;
  }
  if (type === 'boolean') {
    return toBoolean(value, isBooleanAttr(key) && key);
  }
  if (type === 'number') {
    return +value;
  }
  return value;
};

/**
 * Whenever a property on a component changes through a proxy this method will be triggered
 * If the new value is not from a watch, the existing watch on that particular property will be removed
 * This method invokes the defaultPropertyChange handler where the common widget properties like name, class are handled
 * Notifies the component about the style/property change
 */
export const globalPropertyChangeHandler = (component: BaseComponent, key: string, nv: any) => {
  if (!component.getNativeElement()) { // TODO: remove this check later. Without this check throws error on form adv settings dialog on widgetType change on fields.
    return;
  }
  const widgetId = component.widgetId;
  const ov = get(component, key);

  // if the change is not from the bound watch, remove the existing watch
  if (!isChangeFromWatch()) {
    $unwatch(getWatchIdentifier(widgetId, key));
  }

  resetChangeFromWatch();

  const widgetProps = getWidgetPropsByType(component.getWidgetSubType());
  const propInfo = widgetProps?.[key] || get(component.widgetProps, key);
  if (propInfo) {
    const type = propInfo.type;
    if (type) {
      nv = parseValue(key, nv, type);
    }
  }

  // Set the value in the component and trigger appDigest when there is a change in the value
  if (nv !== ov || isObject(nv) || isObject(ov)) {

    if (isDimensionProp(key)) {
      if (nv !== '') {
        nv = toDimension(nv);
      }
    } else if (startsWith(nv, 'resources/')) {
      const ref: any = component;
      if (ref._parentPrefab_ === undefined) {
        // TODO
        let $compEle = $(component.getNativeElement());
        ref._parentPrefab_ = $compEle.parent().closest('[prefabname][prefabname!="__self__"]').attr('prefabname') || '';
      }
      if (ref._parentPrefab_) {
        nv = `WEB-INF/prefabs/${ref._parentPrefab_}/webapp/${nv}`;
      } else if(propInfo && nv != propInfo.value) {
        nv = './' + nv;
      }
    }

    set(component, key, nv);

    if (isStyle(key)) {
      component.notifyStyleChange(key, nv, ov);
    } else {

      if (key === 'conditionalclass' && !(isString(nv) && nv.startsWith('bind:'))) {
        nv = getConditionalClasses(nv, ov);
      }

      if (propInfo || key === 'active') { // adding active key as to notify for active class on selected widget
        component.notifyPropertyChange(key, nv, ov);
      }
    }

    $appDigest();
  }
};
